import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Form,
  Modal,
  Nav,
  Offcanvas,
  Spinner,
} from "react-bootstrap";
import {
  AlertTriangle,
  Check,
  Clock,
  Download,
  ExternalLink,
  HelpCircle,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Slash,
  Smartphone,
  UploadCloud,
} from "react-feather";
import { Link, useSearchParams } from "react-router";
import { apiErrorMessage, useGet, useGetLazy, usePost } from "../../api";
import {
  IFullAutoCouponsTargetingDetailResponse,
  IFullAutoCouponsTargetingRunDetail,
  IFingerprintSearchResult,
  IKindredDomainGeoResponse,
  IFullAutoCouponsTargetingSDKTest,
  IFullAutoCouponsTargetingSDKTestsResponse,
  IKindredCouponTargetingResponse,
  IWorkPipelineHuntRecord,
  IWorkPipelineItem,
  IWorkPipelineStopTemplateRunBody,
  IWorkPipelineReadiness,
  WorkPipelineBrandStatus,
  WorkPipelineStage,
  WORK_PIPELINE_BRAND_STATUS_LABELS,
  WORK_PIPELINE_STAGE_LABELS,
} from "../../interfaces";
import {
  AdvertiserPanel,
  buildProductTargetPayload,
  CouponWithStatus,
  CreateEntityModal,
  EntityAuditTrail,
  ICouponTargetCountItem,
  ReviewDetailPanel,
  SdkTestResultsCard,
  StatusFilter,
  STATUS_OPTIONS,
  TargetingPanel,
  computeStatus,
  entityStatusBadge,
  filterByStatus,
  downloadCouponsCsv,
  exportFilename,
  statusPriority,
} from "../../components/workbench";
import { SDKTestModal } from "../kindred/sdk-test-modal";
import { CouponIssuesPanel } from "./coupon-issues-panel";
import { TemplateDisabledTags } from "./template-disabled-tags";
import { TemplateHelpPanel } from "./template-help-panel";
import { TemplateRunsPanel } from "./template-runs-panel";

type DrawerTab = "code" | "targeting" | "advertiser" | "audit";
type EntityType = "deal" | "coupon" | "offer";

const DRAWER_TABS: DrawerTab[] = ["code", "targeting", "advertiser", "audit"];
const ENTITY_TYPES: EntityType[] = ["deal", "coupon", "offer"];

/** Snapshot-run polling: cadence, how long to wait for our run to show up in
 *  the active list, and the hard cap before falling back to manual reload. */
const POLL_INTERVAL_MS = 4000;
const POLL_TIMEOUT_MS = 120000;

type SnapshotPhase = "idle" | "running" | "complete" | "timeout";

interface PipelineWorkbenchDrawerProps {
  item: IWorkPipelineItem | null;
  claimedByMe: boolean;
  onClose: () => void;
  onClaim: (id: string) => void;
  onRelease: (id: string) => void;
  onReopen: (id: string) => void;
  onComplete: (id: string) => Promise<boolean>;
  onBroken: (id: string, devFailType?: "checkout-extension" | "apk") => void;
  onNeedsHelp: (item: IWorkPipelineItem) => void;
  onDealMismatch: (item: IWorkPipelineItem) => void;
  onBrandStatus: (id: string, status: WorkPipelineBrandStatus | null) => void;
  onProposeBlock: (item: IWorkPipelineItem) => void;
  onStopRun: (
    id: string,
    correlationId: string,
    body: IWorkPipelineStopTemplateRunBody
  ) => Promise<boolean>;
  onSnapshot: (id: string) => Promise<void>;
  onHunt: (
    id: string,
    payload: {
      note?: string;
      sourcesChecked?: string[];
      checkedAt?: string;
    }
  ) => Promise<void>;
  huntBusy: boolean;
  onRefresh: () => void;
  actionError: string | null;
  onDismissError: () => void;
  busy: boolean;
}

const CHECK_VARIANT: Record<string, string> = {
  green: "success",
  amber: "warning",
  red: "danger",
  error: "danger",
  grey: "secondary",
};

const Stat: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="text-center lh-sm">
    <div className="fw-semibold">{children}</div>
    <div className="text-muted" style={{ fontSize: 10 }}>
      {label}
    </div>
  </div>
);

/** Snapshot is domain-level, so it sits in the header rather than per-code. */
const SnapshotSummary: React.FC<{ facItemId: string }> = ({ facItemId }) => {
  const { data, loading } = useGet<IFullAutoCouponsTargetingDetailResponse>(
    `full-auto-coupons-targeting/detail/${facItemId}`,
    true
  );

  if (loading) return <Spinner animation="border" size="sm" />;
  if (!data) return null;

  const m = data.snapshot?.metrics;
  const pct = (v: number | undefined) => `${Math.round((v ?? 0) * 100)}%`;
  const failing = (data.eligibilityChecks ?? []).filter(
    (c) => c.status !== "green"
  );

  return (
    <div className="d-flex align-items-center gap-3 small">
      <Badge
        bg={CHECK_VARIANT[data.overallStatus] ?? "secondary"}
        title={
          failing.length
            ? failing.map((c) => `${c.label}: ${c.detail}`).join("\n")
            : "All eligibility checks green"
        }
      >
        {data.overallStatus}
        {failing.length > 0 ? ` · ${failing.length}` : ""}
      </Badge>

      {m && (
        <>
          <Stat label="Codes">{m.activeCouponCount}</Stat>
          <Stat label="Metadata">{pct(m.metadataCoveragePct)}</Stat>
          <Stat label="Targeting">{pct(m.targetingCoveragePct)}</Stat>
          <Stat label="Templates">
            {m.hasAutoTemplate ? "Auto" : "—"}/{m.hasSkuTemplate ? "SKU" : "—"}
          </Stat>
          <Stat label="SDK test">
            {m.lastSdkTestResult ? (
              <span
                className={
                  m.lastSdkTestResult === "pass"
                    ? "text-success"
                    : "text-danger"
                }
                title={
                  m.lastSdkTestAt
                    ? new Date(m.lastSdkTestAt).toLocaleDateString()
                    : undefined
                }
              >
                {m.lastSdkTestResult}
              </span>
            ) : (
              "—"
            )}
          </Stat>
        </>
      )}

      <Link
        to={`/kindred/full-auto-coupons-targeting/${facItemId}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Snapshot <ExternalLink size={11} />
      </Link>
    </div>
  );
};

/** The most recent APK test, so a tester can see it without opening the
 *  recorder (and without waiting for a snapshot). */
const LastApkTest: React.FC<{
  test: IFullAutoCouponsTargetingSDKTest | null;
}> = ({ test }) => {
  if (!test) return null;
  return <SdkTestResultsCard test={test} className="mb-3" />;
};

/** Every logged "no codes found" for this brand, not just the latest, so a
 *  tester can see what has already been tried before hunting again. */
const HuntHistory: React.FC<{ hunts: IWorkPipelineHuntRecord[] }> = ({
  hunts,
}) => {
  if (!hunts.length) return null;

  return (
    <Alert variant="light" className="py-2 small border">
      <div className="d-flex align-items-center gap-2 mb-1">
        <Search size={13} />
        <strong>
          Code hunts ({hunts.length}
          {hunts.length === 20 ? "+" : ""})
        </strong>
      </div>
      <div style={{ maxHeight: 160, overflow: "auto" }}>
        {hunts.map((h) => (
          <div key={h._id} className="border-top pt-1 mt-1">
            <span className="text-muted">
              {new Date(h.createdAt).toLocaleString()} · {h.actorName}
            </span>
            <div>{h.detail}</div>
          </div>
        ))}
      </div>
    </Alert>
  );
};

export const PipelineWorkbenchDrawer: React.FC<
  PipelineWorkbenchDrawerProps
> = ({
  item,
  claimedByMe,
  onClose,
  onClaim,
  onRelease,
  onReopen,
  onComplete,
  onBroken,
  onNeedsHelp,
  onDealMismatch,
  onBrandStatus,
  onProposeBlock,
  onStopRun,
  onSnapshot,
  onHunt,
  huntBusy,
  onRefresh,
  actionError,
  onDismissError,
  busy,
}) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("LIVE");
  const [showAddCode, setShowAddCode] = useState(false);
  const [showHunt, setShowHunt] = useState(false);
  const [huntNote, setHuntNote] = useState("");
  const [huntSources, setHuntSources] = useState("");
  const [huntCheckedAt, setHuntCheckedAt] = useState("");

  // Tab + selected code live in the query string so a refresh restores them.
  const [searchParams, setSearchParams] = useSearchParams();

  const patchParams = useCallback(
    (patch: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        // replace: tab clicks shouldn't each add a history entry.
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const tabParam = searchParams.get("tab") as DrawerTab | null;
  const tab: DrawerTab =
    tabParam && DRAWER_TABS.includes(tabParam) ? tabParam : "code";
  const setTab = useCallback(
    (next: DrawerTab) => patchParams({ tab: next }),
    [patchParams]
  );

  const entityIdParam = searchParams.get("entity");
  const entityTypeParam = searchParams.get("entityType") as EntityType | null;
  const selectedEntity = useMemo(
    () =>
      entityIdParam && entityTypeParam && ENTITY_TYPES.includes(entityTypeParam)
        ? { type: entityTypeParam, id: entityIdParam }
        : null,
    [entityIdParam, entityTypeParam]
  );
  const setSelectedEntity = useCallback(
    (next: { type: EntityType; id: string } | null) =>
      patchParams(
        next
          ? { entity: next.id, entityType: next.type }
          : { entity: null, entityType: null }
      ),
    [patchParams]
  );

  const domainGeoUrl = item
    ? `kindred/by-domain-geo?domain=${encodeURIComponent(item.domain)}&countryCode=${encodeURIComponent(item.countryCode)}`
    : null;
  const {
    data: domainGeo,
    loading,
    refetch: refetchDomainGeo,
  } = useGet<IKindredDomainGeoResponse>(domainGeoUrl, !!item);

  const { data: readiness, refetch: refetchReadiness } =
    useGet<IWorkPipelineReadiness>(
      item ? `work-pipeline/${item._id}/readiness` : null,
      !!item
    );

  // Bumped after a re-snapshot to remount the header summary so it refetches.
  const [snapshotToken, setSnapshotToken] = useState(0);
  // Owned here rather than inside the panels so recording an APK test or a
  // hunt can refetch them directly.
  const { data: lastApkData, refetch: refetchLastApk } =
    useGet<IFullAutoCouponsTargetingSDKTestsResponse>(
      item?.facItemId
        ? `full-auto-coupons-targeting/items/${item.facItemId}/sdk-tests?limit=1`
        : null,
      true
    );
  const { data: huntData, refetch: refetchHunts } = useGet<{
    results: IWorkPipelineHuntRecord[];
  }>(
    item
      ? `work-pipeline/hunts?domain=${encodeURIComponent(
          item.domain
        )}&countryCode=${encodeURIComponent(item.countryCode)}&limit=20`
      : null,
    true
  );
  const [snapshotting, setSnapshotting] = useState(false);
  const [snapshotPhase, setSnapshotPhase] = useState<SnapshotPhase>("idle");
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const runStartedAtRef = useRef(0);
  const runIdRef = useRef<string | null>(null);
  const runItemReq = usePost<
    { status: string; runId: string },
    { qaItemId: string }
  >();
  const { execute: recomputeItem } = usePost<{
    success: boolean;
    stage: WorkPipelineStage;
  }>();
  const { execute: fetchRunDetail } =
    useGetLazy<IFullAutoCouponsTargetingRunDetail>();

  const finishSnapshot = useCallback(() => {
    setSnapshotPhase("complete");
    setSnapshotToken((t) => t + 1);
    refetchReadiness().catch(() => undefined);
    refetchDomainGeo().catch(() => undefined);
    if (item?._id) {
      recomputeItem(`work-pipeline/${item._id}/recompute`)
        .catch(() => undefined)
        .finally(onRefresh);
    } else {
      onRefresh();
    }
  }, [item?._id, recomputeItem, refetchReadiness, refetchDomainGeo, onRefresh]);

  // Closing the drawer (or switching item) tears the poller down.
  useEffect(() => {
    setSnapshotPhase("idle");
    setSnapshotError(null);
    runIdRef.current = null;
  }, [item?._id]);

  useEffect(() => {
    if (snapshotPhase !== "running" || !item?.facItemId) return;

    let cancelled = false;
    const startedAt = runStartedAtRef.current;

    const poll = async () => {
      try {
        if (!runIdRef.current) return;
        const detail = await fetchRunDetail(
          `full-auto-coupons-targeting/runs/${runIdRef.current}`
        );
        if (cancelled) return;
        if (detail.run.status === "completed") {
          finishSnapshot();
          return;
        }
        if (
          detail.run.status === "failed" ||
          detail.run.status === "cancelled"
        ) {
          setSnapshotError(
            detail.run.status === "failed"
              ? "The snapshot run failed."
              : "The snapshot run was cancelled."
          );
          setSnapshotPhase("timeout");
          return;
        }
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          setSnapshotPhase("timeout");
        }
      } catch (err: any) {
        if (cancelled) return;
        setSnapshotError(
          apiErrorMessage(err, "Could not read the snapshot run status.")
        );
        setSnapshotPhase("timeout");
      }
    };

    const timer = window.setInterval(poll, POLL_INTERVAL_MS);
    poll();
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [snapshotPhase, item?.facItemId, fetchRunDetail, finishSnapshot]);

  const runSnapshot = async () => {
    if (!item) return;
    setSnapshotting(true);
    setSnapshotError(null);
    try {
      if (item.facItemId) {
        // 202 fire-and-forget; progress is tracked via the active-runs poll.
        runStartedAtRef.current = Date.now();
        const queued = await runItemReq.execute(
          "full-auto-coupons-targeting/runs/item",
          {
            qaItemId: item.facItemId,
          }
        );
        runIdRef.current = queued.runId;
        setSnapshotPhase("running");
      } else {
        // Not tracked yet — this enrols the brand and snapshots it inline.
        await onSnapshot(item._id);
        finishSnapshot();
      }
    } catch (err: any) {
      setSnapshotError(apiErrorMessage(err, "Could not start the snapshot."));
      setSnapshotPhase("idle");
    } finally {
      setSnapshotting(false);
    }
  };

  const reloadSnapshot = () => {
    setSnapshotError(null);
    finishSnapshot();
  };

  const allCoupons = useMemo<CouponWithStatus[]>(() => {
    const list = (domainGeo?.coupons ?? []).map((c) => ({
      ...c,
      _status: computeStatus(c.publishedStatus, c.validFrom, c.validTo),
    }));
    return list.sort(
      (a, b) => statusPriority[a._status] - statusPriority[b._status]
    );
  }, [domainGeo]);

  const coupons = useMemo(
    () => filterByStatus(allCoupons, statusFilter),
    [allCoupons, statusFilter]
  );

  // Stable key so the counts fetch only re-fires when the coupon set changes.
  const couponKey = useMemo(
    () => allCoupons.map((c) => c.id).join(","),
    [allCoupons]
  );
  const [targetCounts, setTargetCounts] = useState<Record<string, number>>({});
  const [targetedProductKeys, setTargetedProductKeys] = useState<Set<string>>(
    () => new Set()
  );
  const { data: selectedTargeting } = useGet<IKindredCouponTargetingResponse>(
    selectedEntity?.type === "coupon" && item?.domain
      ? `kindred/targeting/coupons/${selectedEntity.id}?domain=${encodeURIComponent(item.domain)}`
      : null,
    true
  );

  useEffect(() => {
    if (!selectedTargeting) return;
    setTargetedProductKeys(
      new Set(
        selectedTargeting.targets
          .map(
            (target) => target.canonicalKey || target.productMerchantProductId
          )
          .filter((key): key is string => !!key)
      )
    );
  }, [selectedTargeting]);
  const targetCountsPost = usePost<
    { results: ICouponTargetCountItem[] },
    any
  >();
  const addTargetPost = usePost();

  // Advertiser tab targets whichever code is selected in the rail.
  const addProductTarget = async (match: IFingerprintSearchResult) => {
    if (selectedEntity?.type !== "coupon" || !item?.domain) return;
    await addTargetPost.execute(
      `kindred/targeting/coupons/${selectedEntity.id}/targets`,
      { data: buildProductTargetPayload(match, item.domain) }
    );
    setTargetCounts((prev) => ({
      ...prev,
      [selectedEntity.id]: (prev[selectedEntity.id] ?? 0) + 1,
    }));
    setTargetedProductKeys((prev) => {
      const next = new Set(prev);
      next.add(match.product.canonicalKey || match.product.id);
      return next;
    });
  };

  useEffect(() => {
    if (!item?.domain || !couponKey) {
      setTargetCounts({});
      return;
    }
    (async () => {
      try {
        const res = await targetCountsPost.execute(
          "kindred/targeting/coupon-counts",
          {
            domain: item.domain,
            coupons: allCoupons.map((c) => ({
              id: c.id,
              brandId: c.brandId,
              code: c.code,
            })),
          }
        );
        const map: Record<string, number> = {};
        for (const r of res.results ?? []) map[r.id] = r.targetCount;
        setTargetCounts(map);
      } catch {
        setTargetCounts({});
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.domain, couponKey]);

  const blockers = (readiness?.issues ?? []).filter(
    (i) => i.severity === "blocker"
  );
  const warnings = (readiness?.issues ?? []).filter(
    (i) => i.severity === "warning"
  );
  const canComplete = !!readiness?.ready;
  const isComplete = item?.stage === WorkPipelineStage.Complete;
  // Who the claimer is waiting on; while set they can't finish, release or mark it broken.
  const waitingOn =
    item?.devEscalatedAt && !item.devFeedbackAt
      ? "a dev fix"
      : item?.escalatedTo
        ? "Help"
        : item?.dealMismatchAt
          ? "Deals"
          : null;
  // A Coupon Performance review only takes Broken at its APK step, in APK testing.
  const brokenAllowed =
    !item?.couponReviewStep ||
    (item.couponReviewStep === "apk" &&
      item.stage === WorkPipelineStage.ApkTesting);
  const [showApkTest, setShowApkTest] = useState(false);
  const canRecordApkTest =
    !!item?.facItemId &&
    (item?.stage === WorkPipelineStage.ApkTesting ||
      item?.stage === WorkPipelineStage.DevFixes);

  const submitHunt = async () => {
    if (!item) return;
    await onHunt(item._id, {
      note: huntNote,
      sourcesChecked: huntSources
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      checkedAt: huntCheckedAt || undefined,
    });
    // The hunt is logged at this point, so refresh the history even if the
    // completion below is refused.
    refetchHunts().catch(() => undefined);
    const completed = await onComplete(item._id);
    if (!completed) return;
    setShowHunt(false);
    setHuntNote("");
    setHuntSources("");
    setHuntCheckedAt("");
  };

  return (
    <Offcanvas
      show={!!item}
      onHide={onClose}
      placement="end"
      style={{ width: "95vw" }}
    >
      <Offcanvas.Header closeButton>
        <div className="d-flex align-items-center justify-content-between w-100 gap-3 pe-2">
          <div>
            <Offcanvas.Title className="mb-0">
              {item?.brandName || item?.domain}
            </Offcanvas.Title>
            <small className="text-muted">
              {item?.domain} · {item?.countryCode} ·{" "}
              <span className="text-uppercase">
                {item ? WORK_PIPELINE_STAGE_LABELS[item.stage] : ""}
              </span>
            </small>
            {item && <TemplateDisabledTags item={item} className="ms-2" />}
          </div>
          <div className="d-flex align-items-center gap-3">
            {item?.facItemId && (
              <SnapshotSummary key={snapshotToken} facItemId={item.facItemId} />
            )}
            {canRecordApkTest && (
              <Button
                size="sm"
                variant="primary"
                onClick={() => setShowApkTest(true)}
              >
                <Smartphone size={13} className="me-1" />
                Record APK Test
              </Button>
            )}
            <Button
              size="sm"
              variant="outline-info"
              disabled={
                busy || snapshotting || snapshotPhase === "running" || !item
              }
              onClick={runSnapshot}
              title="Re-run the snapshot so coverage, priorities and blockers reflect current data"
            >
              {snapshotting || snapshotPhase === "running" ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <UploadCloud size={13} className="me-1" />
              )}
              {item?.facItemId ? "Re-snapshot" : "Snapshot"}
            </Button>
          </div>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body>
        {actionError && (
          <Alert
            variant="danger"
            dismissible
            onClose={onDismissError}
            className="py-2"
          >
            {actionError}
          </Alert>
        )}

        {snapshotError && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setSnapshotError(null)}
            className="py-2"
          >
            {snapshotError}
          </Alert>
        )}

        {snapshotPhase === "running" && (
          <Alert
            variant="info"
            className="py-2 d-flex align-items-center gap-2"
          >
            <Spinner animation="border" size="sm" />
            <span className="flex-grow-1">
              Snapshot running… coverage and blockers refresh automatically when
              it finishes.
            </span>
          </Alert>
        )}

        {snapshotPhase === "complete" && (
          <Alert
            variant="success"
            dismissible
            onClose={() => setSnapshotPhase("idle")}
            className="py-2 d-flex align-items-center gap-2"
          >
            <Check size={14} />
            <span className="flex-grow-1">
              Snapshot complete — coverage and blockers refreshed.
            </span>
          </Alert>
        )}

        {snapshotPhase === "timeout" && (
          <Alert
            variant="warning"
            className="py-2 d-flex align-items-center gap-2"
          >
            <Clock size={14} />
            <span className="flex-grow-1">
              Snapshot is still running after 2 minutes. Reload once it
              finishes.
            </span>
            <Button
              size="sm"
              variant="outline-warning"
              onClick={reloadSnapshot}
            >
              <RefreshCw size={13} className="me-1" /> Reload
            </Button>
          </Alert>
        )}

        {/* Status actions — mirrors the row dropdown, kept in-context. */}
        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
          {item?.assignedToName ? (
            <Badge bg="warning" text="dark">
              In progress · {item.assignedToName}
            </Badge>
          ) : (
            <Badge bg="light" text="dark">
              Available
            </Badge>
          )}

          {isComplete ? (
            <Button
              size="sm"
              variant="outline-primary"
              disabled={busy}
              onClick={() => item && onReopen(item._id)}
            >
              <RotateCcw size={13} className="me-1" /> Reopen
            </Button>
          ) : !item?.assignedTo ? (
            <Button
              size="sm"
              variant="primary"
              disabled={busy}
              onClick={() => item && onClaim(item._id)}
            >
              Claim domain
            </Button>
          ) : claimedByMe ? (
            <ButtonGroup size="sm">
              <Button
                variant="success"
                disabled={busy || !canComplete || !!waitingOn}
                title={
                  waitingOn
                    ? `Waiting for ${waitingOn} — you can finish once they reply`
                    : canComplete
                      ? undefined
                      : "Resolve the blockers below before completing"
                }
                onClick={() => item && onComplete(item._id)}
              >
                <Check size={13} className="me-1" /> Mark complete
              </Button>
              <Button
                variant="outline-secondary"
                disabled={busy || !!waitingOn}
                title={
                  waitingOn
                    ? `Escalated: stays claimed until ${waitingOn} replies or a lead removes the claim`
                    : "Give the claim back, with a note for the next person"
                }
                onClick={() => item && onRelease(item._id)}
              >
                Release
              </Button>
              <Button
                variant="outline-secondary"
                disabled={busy}
                title="Ask the Lead QA Testers for help; you keep the claim"
                onClick={() => item && onNeedsHelp(item)}
              >
                <HelpCircle size={13} className="me-1" /> Need assistance
              </Button>
              <Button
                variant="outline-warning"
                disabled={busy || !!item?.dealMismatchAt}
                title="Send a merge or mismatch issue to Deals; you keep the claim"
                onClick={() => item && onDealMismatch(item)}
              >
                <AlertTriangle size={13} className="me-1" /> Escalate to Deals
              </Button>
              {brokenAllowed && (
                <Button
                  variant="outline-danger"
                  disabled={busy || !!waitingOn}
                  title="Send it to Dev fixes; you keep the claim and retest after the fix"
                  onClick={() =>
                    item &&
                    onBroken(
                      item._id,
                      item.stage === WorkPipelineStage.ApkTesting
                        ? "apk"
                        : undefined
                    )
                  }
                >
                  <AlertTriangle size={13} className="me-1" /> Broken
                </Button>
              )}
            </ButtonGroup>
          ) : (
            <small className="text-muted">
              Claimed by {item.assignedToName}
            </small>
          )}

          <Form.Select
            size="sm"
            style={{ width: 200 }}
            value={item?.brandStatus ?? ""}
            disabled={busy || !item}
            onChange={(e) =>
              item &&
              onBrandStatus(
                item._id,
                (e.target.value || null) as WorkPipelineBrandStatus | null
              )
            }
          >
            <option value="">Brand status —</option>
            {Object.values(WorkPipelineBrandStatus).map((s) => (
              <option key={s} value={s}>
                {WORK_PIPELINE_BRAND_STATUS_LABELS[s]}
              </option>
            ))}
          </Form.Select>

          {claimedByMe && (
            <Button
              size="sm"
              variant="outline-warning"
              disabled={busy || huntBusy}
              onClick={() => setShowHunt(true)}
            >
              <Slash size={13} className="me-1" /> No codes found
            </Button>
          )}
        </div>

        <LastApkTest test={lastApkData?.tests?.[0] ?? null} />

        <HuntHistory hunts={huntData?.results ?? []} />

        {item && (
          <TemplateRunsPanel
            item={item}
            claimedByMe={claimedByMe}
            canComplete={canComplete && !waitingOn}
            busy={busy}
            onComplete={() => onComplete(item._id)}
            onProposeBlock={() => onProposeBlock(item)}
            onStopRun={(correlationId, body) => onStopRun(item._id, correlationId, body)}
          />
        )}

        {item && (
          <TemplateHelpPanel
            key={`${item._id}-${item.stageReason}-${item.templateHelp?.stage ?? ""}`}
            item={item}
            claimedByMe={claimedByMe}
            onChanged={onRefresh}
          />
        )}

        {item && (
          <CouponIssuesPanel
            key={`${item._id}-${item.stageReason}`}
            itemId={item._id}
            onChanged={() => {
              refetchReadiness().catch(() => undefined);
              onRefresh();
            }}
          />
        )}

        {blockers.length > 0 && (
          <Alert variant="danger" className="py-2">
            <strong className="d-block mb-1">
              Cannot complete — {blockers.length} blocker
              {blockers.length === 1 ? "" : "s"}
            </strong>
            <ul className="mb-0 ps-3 small">
              {blockers.map((b, i) => (
                <li key={`${b.code}-${i}`}>
                  {b.message}
                  {b.entityLabel ? ` (${b.entityLabel})` : ""}
                </li>
              ))}
            </ul>
          </Alert>
        )}
        {warnings.length > 0 && (
          <Alert variant="warning" className="py-2">
            <ul className="mb-0 ps-3 small">
              {warnings.map((w, i) => (
                <li key={`${w.code}-${i}`}>
                  {w.message}
                  {w.entityLabel ? ` (${w.entityLabel})` : ""}
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {loading ? (
          <div className="text-center p-4">
            <Spinner animation="border" size="sm" /> Loading codes…
          </div>
        ) : (
          <div className="d-flex gap-3 align-items-start">
            {/* Codes rail */}
            <div style={{ width: 250, flexShrink: 0 }}>
              <div className="d-flex align-items-center gap-1 mb-2">
                <Form.Select
                  size="sm"
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value as StatusFilter)
                  }
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label} (
                      {o.value === "ALL"
                        ? allCoupons.length
                        : allCoupons.filter((c) => c._status === o.value)
                            .length}
                      )
                    </option>
                  ))}
                </Form.Select>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => refetchDomainGeo()}
                  title="Reload codes"
                >
                  <RefreshCw size={13} />
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={!allCoupons.length}
                  onClick={() =>
                    downloadCouponsCsv(
                      allCoupons,
                      exportFilename("coupons", item?.domain, item?.countryCode)
                    )
                  }
                  title={`Download all ${allCoupons.length} codes on this domain as CSV, every status, with their current and published status`}
                >
                  <Download size={13} />
                </Button>
              </div>

              <Button
                size="sm"
                variant="outline-primary"
                className="w-100 mb-2"
                disabled={!item}
                onClick={() => setShowAddCode(true)}
              >
                <Plus size={13} className="me-1" /> Add code
              </Button>

              {coupons.length === 0 && (
                <div className="text-muted small">
                  No coupons with this status.
                </div>
              )}
              {coupons.map((c) => {
                const active =
                  selectedEntity?.type === "coupon" &&
                  selectedEntity.id === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`btn btn-sm w-100 text-start mb-1 ${
                      active ? "btn-secondary" : "btn-outline-secondary"
                    }`}
                    onClick={() =>
                      setSelectedEntity({ type: "coupon", id: c.id })
                    }
                  >
                    <div className="d-flex justify-content-between align-items-center gap-1">
                      <span className="fw-semibold text-truncate">
                        {c.code}
                      </span>
                      <Badge
                        bg={targetCounts[c.id] > 0 ? "success" : "secondary"}
                        title={`${targetCounts[c.id] ?? 0} target(s)`}
                      >
                        {targetCountsPost.loading
                          ? "…"
                          : (targetCounts[c.id] ?? 0)}
                      </Badge>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <small className="text-truncate">
                        {c.summary || "—"}
                      </small>
                      {entityStatusBadge(c._status)}
                    </div>
                    {c.source && (
                      <Badge
                        bg={
                          c.source.toLowerCase().startsWith("kindred")
                            ? "info"
                            : "light"
                        }
                        text={
                          c.source.toLowerCase().startsWith("kindred")
                            ? undefined
                            : "dark"
                        }
                        className="mt-1"
                        title={`Source: ${c.source}`}
                      >
                        {c.source.trim()}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Tabbed workspace */}
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <Nav
                variant="tabs"
                activeKey={tab}
                onSelect={(k) => setTab((k as DrawerTab) ?? "code")}
              >
                <Nav.Item>
                  <Nav.Link eventKey="code">Code</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="targeting">Targeting</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="advertiser">Advertiser</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="audit">Audit</Nav.Link>
                </Nav.Item>
              </Nav>

              {tab === "code" &&
                (selectedEntity ? (
                  <div className="pt-3">
                    <ReviewDetailPanel
                      entityType={selectedEntity.type}
                      entityId={selectedEntity.id}
                      countryCode={item?.countryCode ?? null}
                      readOnly={!claimedByMe}
                      onClose={() => setSelectedEntity(null)}
                      onSaved={refetchDomainGeo}
                    />
                  </div>
                ) : (
                  <div className="text-muted small pt-3">
                    Select a code to edit its summary, dates, domains, metadata
                    and coverage.
                  </div>
                ))}

              {tab === "targeting" &&
                (selectedEntity?.type === "coupon" ? (
                  <div className="pt-3">
                    <TargetingPanel
                      domain={item?.domain ?? null}
                      // Not just LIVE: the rail can select any status, and the
                      // panel drops a selection that isn't in this list.
                      liveCoupons={allCoupons}
                      enabled={!!item}
                      initialCouponId={selectedEntity.id}
                      hideCouponList
                      hideAdvertiser
                      readOnly={!claimedByMe}
                    />
                  </div>
                ) : (
                  <div className="text-muted small pt-3">
                    Select a code on the left to configure its targeting.
                  </div>
                ))}

              {tab === "advertiser" && item?.domain && (
                <div className="pt-3">
                  <AdvertiserPanel
                    domain={item.domain}
                    addTargetDisabled={selectedEntity?.type !== "coupon"}
                    readOnly={!claimedByMe}
                    targetedProductKeys={targetedProductKeys}
                    onAddProductTarget={addProductTarget}
                  />
                </div>
              )}

              {tab === "audit" &&
                (selectedEntity ? (
                  <div className="pt-3">
                    <EntityAuditTrail
                      entityType={`${selectedEntity.type}s`}
                      entityId={selectedEntity.id}
                      compact
                    />
                  </div>
                ) : (
                  <div className="text-muted small pt-3">
                    Select a code on the left to see its change history.
                  </div>
                ))}
            </div>
          </div>
        )}
      </Offcanvas.Body>

      {item?.facItemId && (
        <SDKTestModal
          show={showApkTest}
          itemId={item.facItemId}
          domain={item.domain}
          countryCode={item.countryCode}
          onClose={() => setShowApkTest(false)}
          onSaved={() => {
            refetchLastApk().catch(() => undefined);
            refetchReadiness().catch(() => undefined);
          }}
        />
      )}

      <CreateEntityModal
        show={showAddCode}
        onHide={() => setShowAddCode(false)}
        only="coupon"
        prefill={{
          brandId: item?.brandId ?? "",
          brandName: item?.brandName ?? "",
          domains: item?.domain ? [item.domain] : [],
          countries: item?.countryCode ? [item.countryCode] : [],
        }}
        onCreated={(_type, newId) => {
          setShowAddCode(false);
          setSelectedEntity({ type: "coupon", id: newId });
          setTab("code");
          refetchDomainGeo();
        }}
      />

      {/* Code hunt with no result — releases the claim, stamps last-checked. */}
      <Modal show={showHunt} onHide={() => setShowHunt(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>No codes found</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            Log what you tried for{" "}
            <strong>{item?.brandName || item?.domain}</strong> so the next
            tester doesn't repeat it.
          </p>
          <Form.Group className="mb-3">
            <Form.Label className="small mb-1">Last checked</Form.Label>
            <Form.Control
              type="date"
              value={huntCheckedAt}
              onChange={(e) => setHuntCheckedAt(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="small mb-1">Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="What did you search, and what came back?"
              value={huntNote}
              onChange={(e) => setHuntNote(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="small mb-1">Sources checked</Form.Label>
            <Form.Control
              placeholder="Comma separated, e.g. brand site, newsletter, Honey"
              value={huntSources}
              onChange={(e) => setHuntSources(e.target.value)}
            />
            <div className="mt-2 d-flex flex-wrap gap-1">
              {huntSources
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
                .map((s) => (
                  <Badge key={s} bg="secondary">
                    {s}
                  </Badge>
                ))}
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHunt(false)}>
            Cancel
          </Button>
          <Button variant="warning" disabled={huntBusy} onClick={submitHunt}>
            {huntBusy && (
              <Spinner animation="border" size="sm" className="me-1" />
            )}
            Record hunt
          </Button>
        </Modal.Footer>
      </Modal>
    </Offcanvas>
  );
};
